import React, { useState } from 'react';
import {
  Box,
  Image,
  Text,
  Badge,
  VStack,
  HStack,
  Button,
  Avatar,
  Heading,
  Icon,
  IconButton,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import { FaHeart, FaComment, FaEye, FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import { Post } from '../types';
import { getUserById, getCategoryById, getBrandById } from '../data/mockData';

interface ItemCardProps {
  post: Post;
  onViewDetails: (postId: string) => void;
  onMessage: (postId: string, sellerId: string) => void;
  variant?: 'grid' | 'list';
}

const ItemCard: React.FC<ItemCardProps> = ({ 
  post, 
  onViewDetails, 
  onMessage, 
  variant = 'grid' 
}) => {
  const [isFavorited, setIsFavorited] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const toast = useToast();

  const seller = getUserById(post.sellerId);
  const category = getCategoryById(post.categoryId);
  const brand = getBrandById(post.brandId);

  const getConditionColor = (condition: string) => {
    const colors = {
      'new': 'green',
      'like-new': 'green',
      'excellent': 'blue',
      'good': 'yellow',
      'fair': 'orange',
      'poor': 'red'
    };
    return colors[condition as keyof typeof colors] || 'gray';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    toast({
      title: isFavorited ? 'Removed from favorites' : 'Added to favorites',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleMessage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMessage(post.id, post.sellerId);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === post.images.length - 1 ? 0 : prev + 1
    );
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? post.images.length - 1 : prev - 1
    );
  };

  if (variant === 'list') {
    return (
      <Box
        bg="white"
        borderRadius="lg"
        shadow="md"
        p={4}
        cursor="pointer"
        onClick={() => onViewDetails(post.id)}
        _hover={{
          shadow: 'lg',
          transform: 'translateY(-2px)',
        }}
        transition="all 0.2s"
        border="1px solid"
        borderColor="gray.200"
      >
        <HStack spacing={4} align="start">
          {/* Image */}
          <Box
            position="relative"
            w="120px"
            h="120px"
            flexShrink={0}
            borderRadius="md"
            overflow="hidden"
          >
            <Image
              src={post.images[0]}
              alt={post.title}
              w="full"
              h="full"
              objectFit="cover"
            />
            {post.images.length > 1 && (
              <Badge
                position="absolute"
                top={2}
                right={2}
                bg="blackAlpha.700"
                color="white"
                fontSize="xs"
              >
                +{post.images.length - 1}
              </Badge>
            )}
          </Box>

          {/* Content */}
          <VStack align="start" flex={1} spacing={2}>
            <HStack justify="space-between" w="full">
              <Heading size="md" noOfLines={1}>
                {post.title}
              </Heading>
              <Text fontSize="xl" fontWeight="bold" color="brand.green.dark">
                ${post.price}
              </Text>
            </HStack>
            
            <HStack spacing={2}>
              <Badge colorScheme={getConditionColor(post.condition)} variant="subtle">
                {post.condition}
              </Badge>
              <Text fontSize="sm" color="gray.600">
                {brand?.name} • {category?.name}
              </Text>
            </HStack>

            <Text color="gray.600" noOfLines={2} fontSize="sm">
              {post.description}
            </Text>

            <HStack justify="space-between" w="full" pt={2}>
              <HStack spacing={4} fontSize="sm" color="gray.500">
                <HStack spacing={1}>
                  <Icon as={FaMapMarkerAlt} />
                  <Text>{post.location}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Icon as={FaClock} />
                  <Text>{formatDate(post.createdAt)}</Text>
                </HStack>
              </HStack>
              
              <HStack spacing={2}>
                <IconButton
                  icon={<FaHeart />}
                  aria-label="Add to favorites"
                  size="sm"
                  variant="ghost"
                  color={isFavorited ? "red.500" : "gray.400"}
                  onClick={handleFavorite}
                  _hover={{ color: "red.500", transform: "scale(1.1)" }}
                />
                <IconButton
                  icon={<FaComment />}
                  aria-label="Message seller"
                  size="sm"
                  variant="ghost"
                  color="brand.green.medium"
                  onClick={handleMessage}
                  _hover={{ color: "brand.green.dark", transform: "scale(1.1)" }}
                />
              </HStack>
            </HStack>
          </VStack>
        </HStack>
      </Box>
    );
  }

  return (
    <Box
      bg="white"
      borderRadius="lg"
      shadow="md"
      overflow="hidden"
      cursor="pointer"
      onClick={() => onViewDetails(post.id)}
      _hover={{
        shadow: 'xl',
        transform: 'translateY(-4px)',
      }}
      transition="all 0.3s"
      border="1px solid"
      borderColor="gray.200"
      position="relative"
    >
      {/* Image Section */}
      <Box position="relative" h="200px">
        <Image
          src={post.images[currentImageIndex]}
          alt={post.title}
          w="full"
          h="full"
          objectFit="cover"
        />
        
        {/* Image Navigation */}
        {post.images.length > 1 && (
          <>
            <IconButton
              icon={<Text>&larr;</Text>}
              aria-label="Previous image"
              position="absolute"
              left={2}
              top="50%"
              transform="translateY(-50%)"
              size="sm"
              bg="blackAlpha.600"
              color="white"
              _hover={{ bg: 'blackAlpha.800' }}
              onClick={handlePrevImage}
            />
            <IconButton
              icon={<Text>&rarr;</Text>}
              aria-label="Next image"
              position="absolute"
              right={2}
              top="50%"
              transform="translateY(-50%)"
              size="sm"
              bg="blackAlpha.600"
              color="white"
              _hover={{ bg: 'blackAlpha.800' }}
              onClick={handleNextImage}
            />
            
            {/* Image Indicators */}
            <HStack
              position="absolute"
              bottom={2}
              left="50%"
              transform="translateX(-50%)"
              spacing={1}
            >
              {post.images.map((_, index) => (
                <Box
                  key={index}
                  w={2}
                  h={2}
                  bg={index === currentImageIndex ? 'white' : 'whiteAlpha.500'}
                  borderRadius="full"
                />
              ))}
            </HStack>
          </>
        )}

        {/* Favorite Button */}
        <IconButton
          icon={<FaHeart />}
          aria-label="Add to favorites"
          position="absolute"
          top={2}
          right={2}
          size="sm"
          bg={isFavorited ? "red.500" : "whiteAlpha.200"}
          color={isFavorited ? "white" : "white"}
          _hover={{ 
            bg: isFavorited ? "red.600" : "red.500",
            color: "white",
            transform: "scale(1.1)"
          }}
          onClick={handleFavorite}
        />

        {/* Condition Badge */}
        <Badge
          position="absolute"
          top={2}
          left={2}
          colorScheme={getConditionColor(post.condition)}
          variant="solid"
          textTransform="capitalize"
        >
          {post.condition}
        </Badge>
      </Box>

      {/* Content Section */}
      <VStack p={4} align="start" spacing={3}>
        <VStack align="start" spacing={1} w="full">
          <Heading size="sm" noOfLines={2} lineHeight="1.2">
            {post.title}
          </Heading>
          <Text fontSize="sm" color="gray.600">
            {brand?.name} • {category?.name}
          </Text>
        </VStack>

        <Text fontSize="2xl" fontWeight="bold" color="brand.green.dark">
          ${post.price}
        </Text>

        <Text color="gray.600" noOfLines={2} fontSize="sm">
          {post.description}
        </Text>

        {/* Seller Info */}
        <HStack spacing={2} w="full">
          <Avatar size="xs" name={`${seller?.firstName} ${seller?.lastName}`} />
          <VStack align="start" spacing={0} flex={1}>
            <Text fontSize="xs" fontWeight="medium">
              {seller?.firstName} {seller?.lastName}
            </Text>
            <HStack fontSize="xs" color="gray.500" spacing={1}>
              <Icon as={FaMapMarkerAlt} />
              <Text>{post.location}</Text>
            </HStack>
          </VStack>
          {seller?.isVerified && (
            <Tooltip label="Verified seller">
              <Badge colorScheme="green" size="sm">
                ✓
              </Badge>
            </Tooltip>
          )}
        </HStack>

        {/* Footer */}
        <HStack justify="space-between" w="full" pt={2}>
          <HStack fontSize="xs" color="gray.500" spacing={1}>
            <Icon as={FaClock} />
            <Text>{formatDate(post.createdAt)}</Text>
          </HStack>
          
          <HStack spacing={2}>
            <Button
              leftIcon={<FaComment />}
              size="sm"
              variant="outline"
              borderColor="brand.green.medium"
              color="brand.green.medium"
              _hover={{ bg: 'brand.green.medium', color: 'white' }}
              onClick={handleMessage}
            >
              Message
            </Button>
            <Button
              leftIcon={<FaEye />}
              size="sm"
              bg="brand.primary"
              color="black"
              _hover={{ bg: 'brand.primaryHover' }}
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(post.id);
              }}
            >
              View
            </Button>
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
};

export default ItemCard; 